import React from 'react';
import { ArrowLeft, Shield, FileText, Globe, Scale } from 'lucide-react';

const Privacy: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    (window as any).navigate?.('/');
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center space-x-4 mb-4">
            <button 
              onClick={goBack}
              className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Privacy Policy & Terms of Use
          </h1>
          <p className="text-gray-300 text-lg">
            Last updated: June 27, 2025 (Originally dated: January 26, 2021)
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Introduction */}
        <section className="mb-12">
          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <FileText className="w-6 h-6 text-blue-400" />
              <h2 className="text-xl font-semibold">Important Notice</h2>
            </div>
            <p className="text-gray-300 leading-relaxed">
              The conditions applicable to the use of this Website ("Terms") are set forth in the following Terms of Use. 
              Before viewing or using this Website, you must have read, understood, and accepted these Terms, as well as 
              the Website Privacy Notice.
            </p>
          </div>
        </section>

        {/* Governing Law */}
        <section className="mb-12">
          <div className="flex items-center space-x-3 mb-6">
            <Scale className="w-6 h-6 text-blue-400" />
            <h2 className="text-2xl font-bold">Governing Law</h2>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <p className="text-gray-300 leading-relaxed">
              Luxembourg law applies to these Terms. Any dispute arising out of or in connection with these Terms 
              will be resolved exclusively by the Luxembourg City Courts.
            </p>
          </div>
        </section>

        {/* Disclaimers */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Disclaimers</h2>
          <div className="bg-gray-800 rounded-lg p-6">
            <p className="text-gray-300 leading-relaxed mb-4">
              This website's use and content are solely for educational reasons. The website provides general 
              information about KEOPS as well as information on the services that KEOPS may offer. Commercial 
              and marketing materials, contact information, and the ability to contact the company via specialized 
              contact forms are all available on the website.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              None of the information or services supplied on the Website are intended to give or be construed 
              as legal or investment advice, or as an enticement of any sort.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Nothing on this Website, in whole or in part, should be construed as creating or constituting a 
              contract, commitment, or obligation on our side.
            </p>
          </div>
        </section>

        {/* Data Protection */}
        <section className="mb-12">
          <div className="flex items-center space-x-3 mb-6">
            <Shield className="w-6 h-6 text-blue-400" />
            <h2 className="text-2xl font-bold">Data Protection</h2>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <p className="text-gray-300 leading-relaxed mb-4">
              KEOPS collects and processes personal data from users of this Website in accordance with the terms 
              of Regulation 2016/679 on the protection of natural people with regard to the processing of personal 
              data and on the free movement of such data (the "GDPR"). We strongly advise you to read the Website 
              Privacy Notice for the whole terms of the processing.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Please see our Cookie Policy page for more information on how this Website utilizes cookies.
            </p>
          </div>
        </section>

        {/* Third Party Websites */}
        <section className="mb-12">
          <div className="flex items-center space-x-3 mb-6">
            <Globe className="w-6 h-6 text-blue-400" />
            <h2 className="text-2xl font-bold">Third Party Websites</h2>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <p className="text-gray-300 leading-relaxed mb-4">
              KEOPS may create hyperlinks between this Website and one or more third-party websites. KEOPS has 
              no responsibility over any other Websites, their contents, or the products or services they provide.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              The availability of any such links does not imply that KEOPS endorses, represents, or warrants 
              such Websites, their contents, their products or services, or their operators.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Your access to and use of such linked Websites is subject to such Websites' terms of service 
              and privacy policies, and you do so at your own risk. KEOPS disclaims responsibility for the 
              privacy policies and practices of third-party internet Websites connected from ours.
            </p>
          </div>
        </section>

        {/* Legal Information */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Legal Information</h2>
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-blue-400 mb-3">Company Details</h3>
              <p className="text-gray-300 leading-relaxed mb-2">
                This Website is owned and managed by KEOPS SARL (hereafter "KEOPS").
              </p>
              <div className="bg-gray-700 rounded p-4 mt-4">
                <p className="text-white font-medium mb-1">KEOPS SARL</p>
                <p className="text-gray-300">12, rue Guillaume Schneider</p>
                <p className="text-gray-300">L-2522 Luxembourg</p>
                <p className="text-gray-300 mt-2">Contact: info@keops.lu</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <p className="text-gray-300 leading-relaxed">
                KEOPS defines the website's Terms of Service. Any mention of "we," "us," or "our" in these 
                Terms refers to "KEOPS."
              </p>
              <p className="text-gray-300 leading-relaxed">
                As a reader, visitor, and/or user of the Website (hereinafter "you"), you are supplied with 
                these Terms of Use. These Terms govern your use of this Website and all of its content 
                (collectively, the "Website"), including but not limited to text, photos, forms, charts and 
                graphs, and video and audio material (which may be amended from time to time).
              </p>
              <p className="text-gray-300 leading-relaxed">
                You are presumed to have recognized and accepted the Terms of this Website by accessing or 
                using it. If you disagree with the Terms, we strongly advise you to leave the Website and 
                not to return.
              </p>
            </div>
          </div>
        </section>

        {/* Copyright & Intellectual Property */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Copyright & Intellectual Property Rights</h2>
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="space-y-4">
              <p className="text-gray-300 leading-relaxed">
                Unless otherwise stated in these Terms, it is strictly prohibited to reproduce, copy, adapt 
                and/or translate, edit, distribute, broadcast, publish, communicate, extract from its database 
                or re-use, create any derivative work, or exploit the Website or its Materials in any way, in 
                whole or in part, on any medium (including electronic medium), without KEOPS' prior written 
                and specific permission.
              </p>
              
              <div className="bg-gray-700 rounded p-4">
                <h3 className="text-lg font-semibold text-blue-400 mb-3">Permitted Use</h3>
                <p className="text-gray-300 leading-relaxed">
                  You are entitled to print and/or temporarily keep copies of the website pages and material 
                  for your own personal use, without any permitted alteration. You may give them to third 
                  parties for their own personal use if you:
                </p>
                <ul className="list-disc list-inside mt-3 space-y-1 text-gray-300">
                  <li>Credit KEOPS as the source</li>
                  <li>Do not use them for any direct or indirect commercial purposes</li>
                  <li>Advise them of these Terms</li>
                </ul>
              </div>
              
              <p className="text-gray-300 leading-relaxed">
                Any unauthorized attempt, by any means and for any reason or purpose, to modify any of this 
                Website's Materials, in whole or in part, or to infiltrate the administrative perimeter of 
                this Website is strictly prohibited. KEOPS retains the right to stop and investigate any 
                such attempt, including referring it to the appropriate authorities for criminal prosecution.
              </p>
              
              <p className="text-gray-300 leading-relaxed">
                Unless otherwise stated, all content on this Website, including but not limited to all text, 
                images, photographs, content, logos, trade name, domain name, video or audio material, source 
                code and machine code, software, database, and the overall design of this Website, as well as 
                the selection, arrangement, and presentation of all materials therein, is original material 
                owned by KEOPS and/or its licensors and protected as such by authors' rights, copyright, 
                trademarks, designs, and the overall design (the "Materials").
              </p>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="mb-12">
          <div className="bg-blue-900 bg-opacity-30 border border-blue-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-400 mb-3">Questions or Concerns?</h3>
            <p className="text-gray-300 leading-relaxed">
              If you have any questions about this Privacy Policy or Terms of Use, please contact us at:
            </p>
            <div className="mt-4 space-y-1">
              <p className="text-white">Email: info@keops.lu</p>
              <p className="text-gray-300">Address: 12, rue Guillaume Schneider, L-2522 Luxembourg</p>
            </div>
          </div>
        </section>

        {/* Back to Top */}
        <div className="text-center">
          <button
            onClick={scrollToTop}
            className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors duration-300"
          >
            <span>Back to Top</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
